/**
 * Fachliche Basis der Abweichungs-Visualisierung.
 *
 * Gegenstück zur PHP-Klasse Goehler_Tool\Public\Report\Deviation. Die
 * Schwellenwerte werden nicht hier definiert, sondern aus PHP übergeben, damit
 * Tool und E-Mail-Bericht denselben Saldo niemals unterschiedlich bewerten.
 *
 * Saldo-Definition: effektive Arbeitszeit minus Prognose. Ein positiver Saldo
 * bedeutet Mehrzeit gegenüber der Prognose.
 */


import { Deviation_Config } from '../../types/report'

/**
 * Bewertung eines Saldos. Dient im Markup als data-status und wird über SCSS
 * eingefärbt, damit die Farbwerte nicht im TypeScript liegen.
 */
export type Deviation_Rating = 'over' | 'under' | 'tolerance' | 'neutral'

/**
 * Aus PHP übergebene Schwellenwerte.
 */
let config: Deviation_Config | null = null

/**
 * Übernimmt die aus PHP übergebenen Schwellenwerte.
 *
 * Wird einmalig beim Start der Anwendung aufgerufen. Bewusst ohne Fallback-
 * Werte: Fehlt die Konfiguration, soll das auffallen und nicht still zu einer
 * anderen Bewertung führen als im Bericht.
 *
 * @param values Schwellenwerte aus Deviation::get_config().
 */
export function set_deviation_config( values: Deviation_Config ): void {
    config = values
}

/**
 * Gibt die Schwellenwerte zurück.
 *
 * @throws Error Wenn die Konfiguration nicht gesetzt wurde.
 *
 * @returns Die Schwellenwerte.
 */
export function get_deviation_config(): Deviation_Config {
    if ( ! config ) {
        throw new Error( 'Deviation-Konfiguration wurde nicht gesetzt.' )
    }

    return config
}

/**
 * Bewertet einen Saldo nach der Ampel-Logik.
 *
 * Reihenfolge der Prüfung: Punktlandung vor Toleranz, danach die Richtung. Ein
 * Saldo von exakt null ist damit neutral und nicht Toleranz. Die Toleranz gilt
 * inklusive: Bei einem Toleranzwert von 30 zählen genau 30 Minuten noch dazu.
 *
 * @param minutes   Saldo in Minuten.
 * @param tolerance Toleranz in Minuten, 0 für keine Toleranz.
 *
 * @returns Die Bewertung des Saldos.
 */
export function get_deviation_rating( minutes: number, tolerance = 0 ): Deviation_Rating {
    if ( minutes === 0 ) {
        return 'neutral'
    }

    if ( tolerance > 0 && Math.abs( minutes ) <= tolerance ) {
        return 'tolerance'
    }

    return minutes > 0 ? 'over' : 'under'
}

/**
 * Begrenzt einen Saldo auf das Skalenmaximum.
 *
 * Das Vorzeichen wird vor der Begrenzung abgetrennt, damit ausschließlich mit
 * Absolutwerten gerechnet wird.
 *
 * @param minutes Saldo in Minuten.
 *
 * @returns Saldo innerhalb der Skala.
 */
export function clamp_deviation( minutes: number ): number {
    if ( minutes === 0 ) {
        return 0
    }

    const sign = minutes > 0 ? 1 : -1

    return sign * Math.min( Math.abs( minutes ), get_deviation_config().max_minutes )
}

/**
 * Prüft, ob ein Saldo die Skala überschreitet und daher geclippt dargestellt wird.
 *
 * @param minutes Saldo in Minuten.
 *
 * @returns Ob der Saldo außerhalb der Skala liegt.
 */
export function is_deviation_clipped( minutes: number ): boolean {
    return Math.abs( minutes ) > get_deviation_config().max_minutes
}

/**
 * Prüft, ob für einen Saldo eine Begründung erforderlich ist.
 *
 * @param minutes Saldo in Minuten.
 *
 * @returns Ob eine Begründung fällig ist.
 */
export function deviation_needs_reason( minutes: number ): boolean {
    return Math.abs( minutes ) >= get_deviation_config().reason_threshold
}

/**
 * Rechnet eine Dauer im Format H:MM in Minuten um.
 *
 * Das Vorzeichen wird vor der Rechnung abgetrennt, da ein führendes Minus bei
 * einer Stundenzahl von null sonst verloren geht und '-0:30' als +30 gelesen würde.
 *
 * @param duration Dauer als H:MM, optional mit führendem Vorzeichen.
 *
 * @returns Dauer in Minuten, 0 bei leerem oder ungültigem Wert.
 */
export function duration_to_minutes( duration: string ): number {
    const trimmed = ( duration ?? '' ).trim()

    if ( trimmed === '' ) {
        return 0
    }

    const sign = trimmed.startsWith( '-' ) ? -1 : 1
    const parts = trimmed.replace( /^[+-]/, '' ).split( ':' )

    const hours = Number.parseInt( parts[ 0 ] ?? '0', 10 )
    const minutes = Number.parseInt( parts[ 1 ] ?? '0', 10 )

    if ( Number.isNaN( hours ) || Number.isNaN( minutes ) ) {
        return 0
    }

    return sign * ( hours * 60 + minutes )
}

/**
 * Formatiert Minuten als Dauer mit explizitem Vorzeichen, z. B. '+2:30'.
 *
 * @param minutes Saldo in Minuten.
 *
 * @returns Formatierte Dauer.
 */
export function format_deviation( minutes: number ): string {
    const sign = minutes < 0 ? '-' : '+'
    const absolute = Math.abs( minutes )
    const hours = Math.floor( absolute / 60 )
    const rest = absolute % 60

    return `${ sign }${ hours }:${ String( rest ).padStart( 2, '0' ) }`
}
